import 'animate.css';
import {useState, useEffect, useCallback} from 'react';
import {Navigate, useNavigate} from 'react-router-dom';
import {Grid} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {CSSTransition, SwitchTransition} from 'react-transition-group';

import styles from 'assets/jss/Login/loginPage';
import BackBtn from '../Buttons/BackBtn';
import {
  useLoginAgent,
  useSendLoginCode,
  useSignupAgent,
} from 'hooks/useRegisterAgent';
import Header from './Header';
import Crm from '../Crm/Crm';
import Form from './Form/Form';
import useTimer from 'hooks/useTimer';
import LoginBanner from 'components/Banner/LoginBanner';
import CallToCrm from './callToCrm';
import {detectPathUrl} from 'helpers/detectPathUrl';
import {useAuthContext} from '../../context/auth/AuthContext';

const useStyles = makeStyles(styles);

export const registerSteps = {
  sendloginCode: 0,
  login: 1,
  signup: 2,
};

const SignIn = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const {isLoggedIn} = useAuthContext();

  const {time, startTimer, isRunning, resetTimer, remainingSeconds} =
    useTimer();

  const login = useLoginAgent();
  const signup = useSignupAgent();
  const sendloginCode = useSendLoginCode();

  const navigateToDashboard = () =>
    navigate(detectPathUrl('app/home'), {replace: true});

  const [userPhone, setUserPhone] = useState([]);
  const [showCrm, setShowCrm] = useState(false);
  const [backButtonClickCount, setBackButtonClickCount] = useState(0);
  const [showTransition, setShowTransition] = useState(false);
  const [steps, setSteps] = useState(registerSteps.sendloginCode);

  const [initialValues, setInitialValues] = useState({
    phone: '',
    code: '',
    // signup
    firstName: '',
    lastName: '',
    isScanCenter: false,
    scanCenterName: null,
    provinceId: null,
    cityId: null,
    address: null,
  });

  const resetCode = () => setInitialValues((prev) => ({...prev, code: ''}));

  const userActivationCodeId = useCallback(
    (phone) => {
      const item =
        userPhone?.length > 0
          ? userPhone.find((item) => item?.phone === phone)
          : null;

      const timestamp = item
        ? ((item?.futurTimeExp - Date.now()) / 1000).toFixed(0)
        : 120;

      const activationCodeIsActive =
        item && timestamp < 120 && timestamp > 0 ? true : false;

      if (item && activationCodeIsActive) {
        resetTimer(timestamp);
        startTimer();
        return {
          activationCodeId: item?.activationCodeId,
          remainingSeconds: timestamp,
        };
      }
      return false;
    },
    [userPhone]
  );

  const sendLoginCodeHandler = useCallback(
    (phone, activationCodeId, remainingSeconds) => {
      const condition = {
        signup:
          login?.data?.data?.user.phoneNumber === phone && remainingSeconds,
        login: activationCodeId && remainingSeconds,
        sendRequest: !activationCodeId && !remainingSeconds,
      };

      if (condition.signup) {
        setShowTransition(!showTransition);
        setSteps(registerSteps.signup);
      } else if (condition.sendRequest) {
        resetCode();
        sendloginCode.mutate({
          phone,
          setUserPhone,
          userPhone,
          setSteps,
          setShowTransition,
          startTimer,
          resetTimer,
          transition: steps === registerSteps.sendloginCode ? true : false,
        });
      } else if (condition.login) {
        setShowTransition(!showTransition);
        setSteps(registerSteps.login);
        resetCode();
      }
    },
    [steps, initialValues]
  );

  const loginHandler = useCallback(
    (code, activationCodeId) => {
      if (code)
        login.mutate({
          activationCodeId,
          code,
          setSteps,
          setShowTransition,
          navigateToDashboard,
          resetTimer,
          isScanCenter: initialValues.isScanCenter,
        });
    },
    [initialValues.isScanCenter]
  );

  const signupHandler = useCallback(
    (firstName, lastName, scanCenterName, cityId, address) => {
      signup.mutate({
        firstName,
        lastName,
        scanCenterName,
        cityId: cityId?.id,
        scanCenterAddress: address,
        isAgent: true,
        isScanCenter: initialValues.isScanCenter,
        setSteps,
        navigateToDashboard,
        resetTimer,
      });
    },
    [initialValues.isScanCenter]
  );

  const timerHandler = () => {
    if (isRunning) return;
    sendLoginCodeHandler(initialValues.phone);
  };

  const timerProps = {
    timerHandler,
    time,
    resetTimer,
    isRunning,
    startTimer,
  };

  const backHandler = useCallback(() => {
    if (steps === registerSteps.sendloginCode) {
      setInitialValues({
        firstName: '',
        lastName: '',
        phone: '',
        code: '',
        isScanCenter: false,
        scanCenterName: null,
        provinceId: null,
        cityId: null,
        address: null,
      });
      setShowCrm(false);
      setBackButtonClickCount(0);
      localStorage.clear();
      window?.ReactNativeWebView?.postMessage('logout');
    } else if (
      steps === registerSteps.login ||
      steps === registerSteps.signup
    ) {
      setSteps(registerSteps.sendloginCode);
    }
  }, [steps]);

  const onActionHandler = useCallback(
    ({code, phone, firstName, lastName, scanCenterName, cityId, address}) => {
      const {activationCodeId, remainingSeconds} = userActivationCodeId(phone);

      if (steps === registerSteps.sendloginCode) {
        sendLoginCodeHandler(phone, activationCodeId, remainingSeconds);
      } else if (steps === registerSteps.login) {
        loginHandler(code, activationCodeId);
      } else {
        if (steps === registerSteps.signup) {
          signupHandler(firstName, lastName, scanCenterName, cityId, address);
        }
      }
    },
    [steps, userPhone]
  );

  const isLoading =
    signup.isLoading || login.isLoading || sendloginCode.isLoading;

  const handlePopstate = (event) => {
    event.preventDefault();
    setBackButtonClickCount(backButtonClickCount + 1);
  };

  const assistantType = (event) => {
    if (event && typeof event.data === 'string') {
      const data = JSON?.parse(event?.data);
      if (data?.state === 'assistantType') {
        const isScanCenter = data.type === 'scanCenter' ? true : false;
        setInitialValues((prev) => ({
          ...prev,
          isScanCenter,
        }));
      }
    }
  };

  useEffect(() => {
    if (backButtonClickCount > 0 && !showCrm) {
      if (steps === registerSteps.login || steps === registerSteps.signup) {
        setSteps(registerSteps.sendloginCode);
      }
    }

    window.history.pushState(null, null, window.location.href);
    window.addEventListener('popstate', handlePopstate);

    // get assiatant type from native
    window?.ReactNativeWebView?.postMessage('getAssistantType');
    document.addEventListener('message', assistantType);
    window.addEventListener('message', assistantType);

    return () => {
      window.removeEventListener('popstate', handlePopstate);
      document.removeEventListener('message', assistantType);
      window.removeEventListener('message', assistantType);
    };
  }, [backButtonClickCount]);

  if (isLoggedIn) return <Navigate to={detectPathUrl('app/home')} replace />;

  return (
    <>
      <LoginBanner crm={showCrm} isScanCenter={initialValues.isScanCenter} />
      <div style={{padding: '0 17px'}}>
        {steps !== registerSteps.signup ? (
          <CallToCrm setShowCrm={setShowCrm} showCrm={showCrm} />
        ) : (
          ''
        )}
        {showCrm ? (
          <Crm showCrm={showCrm} setShowCrm={setShowCrm} />
        ) : (
          <Grid item xs={12} mt='40px'>
            <Grid item sx={{position: 'absolute', left: '16px', top: '18px'}}>
              <BackBtn isLogin onActionHandler={backHandler} />
            </Grid>
            <SwitchTransition mode='out-in'>
              <CSSTransition
                timeout={250}
                key={showTransition}
                classNames={{
                  enterDone: 'animate__animated animate__fadeInRight',
                  enterActive: 'animate__animated animate__fadeInRight',
                }}>
                <Grid container>
                  <Grid item xs={12} className={classes.containerForm}>
                    <Header currentStep={steps} phone={initialValues?.phone} />
                    <Form
                      steps={steps}
                      isLoading={isLoading}
                      timerProps={timerProps}
                      initialValues={initialValues}
                      setInitialValues={setInitialValues}
                      actionHandler={onActionHandler}
                    />
                  </Grid>
                </Grid>
              </CSSTransition>
            </SwitchTransition>
          </Grid>
        )}
      </div>
    </>
  );
};

export default SignIn;
